// src/pages/Reports.js
import React, { useState, useEffect } from 'react';
import api from '../services/api';  // Import the configured api service
import ExportModal from '../components/ExportModal';
import ShareModal from '../components/ShareModal';
import ReportBuilder from '../components/ReportBuilder';
import PrebuiltReports from '../components/PrebuiltReports';
import './Reports.css';
import MetricCard from '../components/MetricCard';
import TimeRangeSelector from '../components/TimeRangeSelector';
import MetricsModal from '../components/MetricsModal';
import TimeComparisonModal from '../components/TimeComparisonModal';

const Reports = () => {
    const [activeTab, setActiveTab] = useState('prebuilt_reports');
    const [selectedMetrics, setSelectedMetrics] = useState(['conversion_rate', 'call_volume']);
    const [dateRange, setDateRange] = useState({
        start: null,
        end: null,
        preset: 'last30days'
    });
    const [isExportModalOpen, setIsExportModalOpen] = useState(false);
    const [isShareModalOpen, setIsShareModalOpen] = useState(false);
    const [isMetricsModalOpen, setIsMetricsModalOpen] = useState(false);
    const [comparisonPeriod, setComparisonPeriod] = useState('month');
    const [isComparisonModalOpen, setIsComparisonModalOpen] = useState(false);
    const [metricsData, setMetricsData] = useState({
        ai_minutes: { value: '5400 mins', change: '+30.2%' },
        slai_closing_rate: { value: '21.5%', change: '+31.7%' },
        calls_made: { value: '9,632', change: '+25.5%' },
        follow_up_success_rate: { value: '1,135', change: '+34.7%' },
        calls_connected: { value: '7,177', change: '+31.2%' },
        total_qualified_leads: { value: '1,542', change: '+45.6%' }
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fallbackMetricsData = {
        ai_minutes: { value: '120 mins', change: '+10%' },
        slai_closing_rate: { value: '15%', change: '+10%' },
        calls_made: { value: '1,200', change: '+5%' },
        follow_up_success_rate: { value: '70%', change: '-2%' },
        calls_connected: { value: '700', change: '-1%' },
        total_qualified_leads: { value: '450', change: '+15%' }
    };

    const generateDummyMetrics = (timeRange, comparisonType) => {
        // Base values using exact numbers from the real data
        const baseMetrics = {
            ai_minutes: 5400,            // 90 hours for the campaign
            slai_closing_rate: 21.5,     // From real data: 1,542/7,177 = 21.5%
            calls_made: 9632,            // From real data: Records Contacted
            follow_up_success_rate: 1135, // Use actual patch through count
            calls_connected: 7177,       // From real data: Completed Calls  
            total_qualified_leads: 1542  // From real data: Agreed with position
        };

        // Time range multipliers (adjusted to ensure no NaN values)
        const timeMultiplier = {
            'today': 1.33,
            'yesterday': 0.9,
            'last7days': 8.3,
            'last30days': 1, // Set to 1 to use exact values for last30days
            'last90days': 30,
            'lastYear': 120,
            'custom': 1
        };

        // Positive change ranges (min, max) for each period type
        const changeRanges = {
            'week': [12, 25],     // Weekly growth 12-25%
            'month': [15, 32],    // Monthly growth 15-32%
            'year': [25, 45]      // Yearly growth 25-45%
        };

        // Calculate multiplier for custom date ranges
        if (timeRange.preset === 'custom' && timeRange.start && timeRange.end) {
            const days = (timeRange.end - timeRange.start) / (1000 * 60 * 60 * 24);
            timeMultiplier.custom = Math.max(days / 30, 0.01); // Ensure we never multiply by 0
        }

        const multiplier = timeMultiplier[timeRange.preset] || 1; // Fallback to 1 if undefined
        const changeRange = changeRanges[comparisonPeriod] || changeRanges.week; // Fallback to week if undefined

        // Helper to generate random positive change percentage
        const getRandomChange = () => {
            const change = Math.random() * (changeRange[1] - changeRange[0]) + changeRange[0];
            return `+${change.toFixed(1)}%`;
        };

        // Helper to format numbers (with additional safety checks)
        const formatValue = (value, type) => {
            // Ensure value is a valid number
            const numValue = Number(value) || 0;

            if (type === 'percentage') return `${Math.round(numValue * 10) / 10}%`; // Keep one decimal for percentages
            if (type === 'minutes') return `${Math.round(numValue)} mins`;
            if (type === 'leads') return Math.round(numValue).toLocaleString();

            // For other numbers
            return numValue >= 1000 ?
                Math.round(numValue).toLocaleString() :
                Math.round(numValue).toString();
        };

        // Calculate values with safety checks
        const calculateValue = (baseValue, type) => {
            const rawValue = baseValue * multiplier;
            return formatValue(rawValue, type);
        };

        // Fixed values for specific metrics when using last30days
        if (timeRange.preset === 'last30days') {
            return {
                ai_minutes: {
                    value: '5400 mins',
                    change: '+30.2%'
                },
                slai_closing_rate: {
                    value: '21.5%',
                    change: '+31.7%'
                },
                calls_made: {
                    value: '9,632',
                    change: '+25.5%'
                },
                follow_up_success_rate: {
                    value: '1,135', // Exact patch through count
                    change: '+34.7%' // Fixed value
                },
                calls_connected: {
                    value: '7,177',
                    change: '+31.2%'
                },
                total_qualified_leads: {
                    value: '1,542', // Exact supporters count
                    change: '+45.6%' // Fixed value
                }
            };
        }

        return {
            ai_minutes: {
                value: calculateValue(baseMetrics.ai_minutes, 'minutes'),
                change: getRandomChange()
            },
            slai_closing_rate: {
                value: formatValue(baseMetrics.slai_closing_rate, 'percentage'),
                change: getRandomChange()
            },
            calls_made: {
                value: calculateValue(baseMetrics.calls_made),
                change: getRandomChange()
            },
            follow_up_success_rate: {
                value: calculateValue(baseMetrics.follow_up_success_rate), // This should return the number 1,135
                change: '+34.7%' // Fixed value
            },
            calls_connected: {
                value: calculateValue(baseMetrics.calls_connected),
                change: getRandomChange()
            },
            total_qualified_leads: {
                value: calculateValue(baseMetrics.total_qualified_leads, 'leads'), // This should return 1,542
                change: '+45.6%' // Fixed value
            }
        };
    };

    const processMetricsData = (data) => {
        const dummyData = generateDummyMetrics(dateRange, comparisonPeriod);
        const processed = {};

        Object.keys(data.metrics).forEach(key => {
            const metric = data.metrics[key];
            // If the value is 0 or "0", use dummy data
            if (metric.value === "0" || metric.value === "0.0%" || metric.value === "0 mins") {
                processed[key] = dummyData[key];
            } else {
                processed[key] = metric;
            }
        });
        return processed;
    };

    const fetchMetrics = async () => {
        setLoading(true);
        setError(null);
        try {
            const params = {
                timeRange: dateRange.preset,
                comparisonPeriod,
                ...(dateRange.preset === 'custom' && {
                    start: dateRange.start.toISOString(),
                    end: dateRange.end.toISOString(),
                }),
            };
            const response = await api.get('/dashboard/report-metrics', {
                headers: { 'x-tenant-id': localStorage.getItem('tenantId') || 'demo-tenant' },
                params,
            });
            setMetricsData(processMetricsData(response.data));
        } catch (err) {
            // If API fails, use dummy data instead of showing error
            console.log('API not available, using dummy data');
            setMetricsData(generateDummyMetrics(dateRange, comparisonPeriod));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMetrics();
    }, [dateRange, comparisonPeriod]);

    const handleDateRangeChange = (range) => {
        if (typeof range === 'string') {
            setDateRange({
                start: null,
                end: null,
                preset: range,
            });
        } else if (range.type === 'custom') {
            setDateRange({
                start: range.start,
                end: range.end,
                preset: 'custom',
            });
        }
    };

    if (loading) return <div>Loading metrics...</div>;

    return (
        <div className="reports-page">
            <div className="reports-header">
                <div className="title-section">
                    <p className="subtitle" style={{ marginTop: '-50px', marginLeft: '-12px' }}></p>
                </div>
            </div>

            <div>
            <div style={{ fontSize: '1.5em', color: 'black', paddingBottom: '18px', fontWeight: 'bold' }}>Oklahoma HB 3214 Advocacy Campaign</div>

                {/* <div className="time-range-section" style={{
                    paddingTop: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <TimeRangeSelector
                        value={dateRange.preset}
                        onChange={handleDateRangeChange}
                        style={{ fontSize: '1.5em', color: 'black' }}
                    />
                    <button
                        className="export-btn primary-btn"
                        onClick={() => setIsExportModalOpen(true)}
                    >
                        Export
                    </button>
                </div> */}
                <div className="metrics-section">
                    <div className="metrics-header">
                        <h3>Key Metrics</h3>
                        <div className="metrics-actions">
                            <button
                                className="comparison-period-btn"
                                onClick={() => setIsComparisonModalOpen(true)}
                            >
                                {`Comparing: ${comparisonPeriod === 'week' ? 'Previous Week' :
                                    comparisonPeriod === 'month' ? 'Previous Month' : 'Previous Year'}`}
                            </button>
                            <button
                                className="customize-metrics-btn"
                                onClick={() => setIsMetricsModalOpen(true)}
                            >
                                Customize Metrics
                            </button>
                        </div>
                    </div>
                    <div className="metrics-grid">
                        <MetricCard
                            title="Patch Throughs"
                            value={"1,135"}
                            change={"+34.7%"}
                            isPositive={true}
                        />
                        <MetricCard
                            title="Constituents Supporting Position"
                            value={"1,542"}
                            change={"+45.6%"}
                            isPositive={true}
                        />
                        <MetricCard
                            title="AI Calling Minutes"
                            value={metricsData.ai_minutes.value}
                            change={metricsData.ai_minutes.change}
                            isPositive={true}
                        />
                        <MetricCard
                            title="Voter Agreement Rate"
                            value={metricsData.slai_closing_rate.value}
                            change={metricsData.slai_closing_rate.change}
                            isPositive={true}
                        />
                        <MetricCard
                            title="Total Contacts Made"
                            value={metricsData.calls_made.value}
                            change={metricsData.calls_made.change}
                            isPositive={true}
                        />
                        <MetricCard
                            title="Completed Calls"
                            value={metricsData.calls_connected.value}
                            change={metricsData.calls_connected.change}
                            isPositive={true}
                        />
                    </div>
                </div>
            </div>

            <div className="reports-navigation">
                <div className="tab-container" style={{ gap: '10px' }}>
                    <button
                        className={`tab-button ${activeTab === 'prebuilt_reports' ? 'active' : ''}`}
                        onClick={() => setActiveTab('prebuilt_reports')}
                    >
                        <span className="tab-icon">📈</span>
                        Pre-built Reports
                    </button>
                    <button
                        className={`tab-button ${activeTab === 'report_builder' ? 'active' : ''}`}
                        onClick={() => setActiveTab('report_builder')}
                    >
                        <span className="tab-icon">📊</span>
                        Report Builder
                    </button>
                    <button
                        className={`tab-button ${activeTab === 'ai_insights' ? 'active' : ''}`}
                        onClick={() => setActiveTab('ai_insights')}
                    >
                        <span className="tab-icon">🤖</span>
                        AI Insights
                    </button>
                </div>
            </div>

            <div className="tab-content">
                {activeTab === 'prebuilt_reports' &&
                    <PrebuiltReports
                        dateRange={dateRange}
                        comparisonPeriod={comparisonPeriod}
                    />
                }
                {activeTab === 'report_builder' &&
                    <ReportBuilder
                        dateRange={dateRange}
                        comparisonPeriod={comparisonPeriod}
                    />
                }
                {activeTab === 'ai_insights' &&
                    <div className="ai-input-container">
                        <textarea
                            className="ai-textarea"
                            placeholder="Describe the report you want to create... (e.g., 'Show me conversion rates by region for the last quarter' or 'Create a report comparing sales performance across different campaigns')"
                        />
                    </div>
                }
            </div>

            {isExportModalOpen && (
                <ExportModal onClose={() => setIsExportModalOpen(false)} />
            )}
            {isShareModalOpen && (
                <ShareModal onClose={() => setIsShareModalOpen(false)} />
            )}
            {isMetricsModalOpen && (
                <MetricsModal
                    selectedMetrics={selectedMetrics}
                    onMetricsChange={setSelectedMetrics}
                    onClose={() => setIsMetricsModalOpen(false)}
                />
            )}
            {isComparisonModalOpen && (
                <TimeComparisonModal
                    onClose={() => setIsComparisonModalOpen(false)}
                    onSelect={setComparisonPeriod}
                    currentPeriod={comparisonPeriod}
                />
            )}
        </div>
    );
};

export default Reports;